import React, { useContext, useMemo } from "react";
import moment from "moment";
import PropTypes from "prop-types";

import { useTranslation } from "@common/hooks/translation";
import TicketSkeleton from "@common/components/ticketSkeleton";
import PromotionalBetMark from "@tickets/components/promotionalBetMark";
import { isCashout } from "@tickets/helpers/actions/isActionAvailable";
import { BetStatus, DetailBetStatus } from "../components/betStatus";
import BetType from "../components/betType";
import Money from "@app/dev/terminal/components/money";
import { BetStateEnum } from "@app/dev/constants/enums/betState";
import { PreferencesContext } from "@common/contexts/preferences";
import OddConverter from "@common/helpers/oddConverter";
import Config from "@app/dev/config/main";

// Server-side per-game combined odd may arrive under different keys depending
// on backend version and bet type (pure BB vs BB-leg-in-Multiple).
const pickCombinedOdd = (selection) =>
  selection.bet_builder_combined_odd
  ?? selection.bet_builder_odd
  ?? selection.combined_odd
  ?? selection.bet_builder_price
  ?? null;

// Derive a single status for a BB card from its legs:
//   any lost → lost; any pending → pending; all won → won.
const deriveCardState = (selections) => {
  const states = selections.map((s) => s.state);
  if (states.includes(3)) return 3;
  if (states.includes(1) || states.includes(0)) return 1;
  if (states.every((v) => v === 4)) return 4;
  return states[0];
};

const normalizeDisplayState = (state) => (state === 0 ? 1 : state);

// Build BB cards from selections that belong to bet builder groups.
// `keyField`: `match_id` for pure BB tickets, `bet_builder_match_id` for
// BB legs inside a Multiple.
export const buildBetBuilderCards = (selections, keyField) => {
  const grouped = new Map();
  selections.forEach((sel) => {
    const key = sel[keyField];
    if (key == null) return;
    if (!grouped.has(key)) {
      grouped.set(key, {
        matchId: key,
        matchName: sel.match_name,
        sportName: sel.sport_name,
        isLive: sel.is_live,
        startTime: sel.start_time,
        combinedOdd: pickCombinedOdd(sel),
        selections: [],
      });
    }
    grouped.get(key).selections.push(sel);
  });
  return Array.from(grouped.values()).map((card) => ({
    ...card,
    state: deriveCardState(card.selections),
  }));
};

export const BetBuilderCard = ({ card }) => {
  const t = useTranslation();
  const [preferences] = useContext(PreferencesContext);

  return (
    <div className="ticket-section T-bb-card">
      <div className="ticket-info-row T-bb-card-fixture">
        <p className="T-bb-card-fixture-name">
          {card.isLive && <span className="live-icon-ticket">{t("Live")}</span>}
          <span>{card.matchName}</span>
        </p>
        <DetailBetStatus data={{ state: normalizeDisplayState(card.state) }} isJustText />
      </div>

      {card.startTime && (
        <div className="ticket-info-row T-bb-card-date">
          <span>{moment(card.startTime).format("DD/MM/YYYY HH:mm:ss")}</span>
        </div>
      )}

      <div className="ticket-info-row T-bb-card-banner">
        <span className="T-bb-card-banner-left">
          <span className="T-bb-card-badge">{t("BB")}</span>
          <span className="T-bb-card-title">{t("Bet Builder")}</span>
        </span>
        {card.combinedOdd != null && (
          <span className="T-bb-card-odd">
            {OddConverter(Config, card.combinedOdd, preferences.oddsFormat)}
          </span>
        )}
      </div>

      <div className="T-bb-card-items">
        {card.selections.map((sel, idx) => {
          const isLast = idx === card.selections.length - 1;
          return (
            <div
              key={sel.selection_id}
              className={`T-bb-card-item${isLast ? " is-last" : ""}`}
            >
              <span className="T-bb-card-item-dot" />
              <div className="T-bb-card-item-body">
                <div className="T-bb-card-item-row">
                  <p className="T-bb-card-item-market">{sel.display_market_name}</p>
                  <DetailBetStatus data={{ state: normalizeDisplayState(sel.state) }} isJustText />
                </div>
                <div className="T-bb-card-item-row">
                  <p className="T-bb-card-item-selection">{sel.display_selection_name}</p>
                </div>
                {sel.match_info && (
                  <div className="T-bb-card-item-row T-bb-card-item-result">
                    <p>{t("Event result")}</p>
                    <p>{sel.match_info}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

BetBuilderCard.propTypes = {
  card: PropTypes.shape({
    matchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    matchName: PropTypes.string,
    sportName: PropTypes.string,
    isLive: PropTypes.bool,
    startTime: PropTypes.string,
    combinedOdd: PropTypes.number,
    state: PropTypes.number,
    selections: PropTypes.array.isRequired,
  }).isRequired,
};

const TicketDetailsBetBuilder = ({ ticket }) => {
  const t = useTranslation();
  const [preferences] = useContext(PreferencesContext);

  const { dataFromSwarm } = ticket;
  const ticketPrice = dataFromSwarm.price === -1 ? t("SP") : dataFromSwarm.price ?? "-";
  const winAmount = dataFromSwarm.win_amount - dataFromSwarm.tax_amount;
  const isPromotionalBet = !!(dataFromSwarm.is_risk_free_bet || dataFromSwarm.free_bet_amount || dataFromSwarm.boost_percentage);
  const hasAmounts = !!(dataFromSwarm.stake_tax_amount && dataFromSwarm.amount);

  // Pure BB ticket: every leg is BB, so group by match_id.
  const cards = useMemo(
    () => buildBetBuilderCards(dataFromSwarm.selection_list || [], "match_id"),
    [dataFromSwarm.selection_list],
  );

  return (
    <TicketSkeleton dark>
      <div className="ticket-section">
        <div className="ticket-info-row">
          <p>{t("Status")}</p>
          <BetStatus data={ticket} />
        </div>
        <div className="ticket-info-row">
          <p>{t("Id")}</p>
          <span>{dataFromSwarm?.id ?? ticket?.id}</span>
        </div>
        <div className="ticket-info-row">
          <p>{t("Date")}</p>
          <span>{moment(dataFromSwarm?.created).format("DD/MM/YYYY HH:mm:ss")}</span>
        </div>
        <div className="ticket-info-row">
          <p>{t("Bet Type")}</p>
          <BetType data={ticket} />
        </div>
      </div>

      {cards.map((card) => (
        <BetBuilderCard key={card.matchId} card={card} />
      ))}

      <div className="ticket-section">
        <div className="ticket-info-row">
          <p>{t("Total odd")}</p>
          <p className="ticket-info-white">
            {OddConverter(Config, ticketPrice, preferences.oddsFormat)}
          </p>
        </div>
        <div className="ticket-info-row">
          <p>{t("Bet Amount")}</p>
          <p className="ticket-info-white">
            <Money amount={dataFromSwarm.amount} />
          </p>
        </div>
        {!!dataFromSwarm.stake_tax_amount && (
          <div className="ticket-info-row">
            <p>{t("Tax on stake")}</p>
            <p className="ticket-info-white">
              <Money amount={dataFromSwarm.stake_tax_amount} />
            </p>
          </div>
        )}
        {(hasAmounts || isPromotionalBet) && (
          <div className="ticket-info-row">
            <p>{t("Total stake")}</p>
            <p className="ticket-info-white">
              <Money amount={dataFromSwarm.stake_tax_amount + dataFromSwarm.amount + (dataFromSwarm.free_bet_amount || 0)} />
              <PromotionalBetMark data={dataFromSwarm} />
            </p>
          </div>
        )}
        {dataFromSwarm.boost_percentage && (
          <div className="ticket-info-row">
            <div className="ticket-additional-info">
              <p>{t("Profit Boost:")}</p>
              <p className="ticket-percentage-info">{`${dataFromSwarm.boost_percentage}%`}</p>
            </div>
            <p className="ticket-info-white">
              <Money amount={dataFromSwarm.bonus_amount} />
            </p>
          </div>
        )}
        {!!dataFromSwarm.cashout_amount && isCashout(ticket) && (
          <div className="ticket-info-row">
            <p>{t("Available cash-out")}</p>
            <p className="ticket-info-white">
              <Money amount={dataFromSwarm.cashout_amount} />
            </p>
          </div>
        )}
        {dataFromSwarm.state_id === BetStateEnum.Accepted && (
          <div className="ticket-info-row">
            <p>{t("Possible win")}</p>
            <p className="ticket-info-white">
              {dataFromSwarm.price === -1
                ? "SP"
                : <Money amount={dataFromSwarm.possible_win - (dataFromSwarm.possible_tax_amount || 0)} />}
            </p>
          </div>
        )}
        {dataFromSwarm.state_id !== BetStateEnum.Accepted && !!winAmount && (
          <div className="ticket-info-row">
            <p>{t("Won amount")}</p>
            <p className="ticket-info-white">
              <Money amount={winAmount} />
            </p>
          </div>
        )}
      </div>
    </TicketSkeleton>
  );
};

TicketDetailsBetBuilder.propTypes = {
  ticket: PropTypes.object.isRequired,
};

export default TicketDetailsBetBuilder;
